import React, { useEffect, useMemo, useState } from "react";
import { Page, LoadingMsg } from "components/common";
import { Row, Col, Button } from "reactstrap";
import { NotificationManager } from "react-notifications";
import {
  FormGroup,
} from "components/common/Forms";
import { LocalForm, Control } from "react-redux-form";
import GetBack from "components/refactoring/BackButton";
import { Trans, useTranslation } from "react-i18next";
import { ZatcaClient } from "services";
import { useHistory } from 'react-router-dom';
import Select from "react-select";
import { ID_TYPES } from "services/util";
import { useXeroClient } from "hooks";
import { useQuery } from "react-query";
import { findLast } from "lodash";


const RegistrationSDK = ({ }) => {

  const { t } = useTranslation();
  const history = useHistory();

  const zatca = ZatcaClient();
  const xero = useXeroClient();

  const { data: organisation} = useQuery(["organisations"], 
    async () => xero.get("organisations").get("data").get(0));

  const {
    AddressLine1,
    AddressLine2,
    AddressLine3,
    AddressLine4,
    City,
    Region,
    PostalCode,
  } = useMemo(() => findLast(organisation?.Addresses, { AddressType: "POBOX" }) || {}, [organisation])

  const [data, setData] = useState();
  const [isSending, setIsSending] = useState(false);

  const idTypes = useMemo(() => ID_TYPES, [])

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await zatca.get("sdk");
        setData(response.data);
      } catch (error) {
        console.error("Error fetching data:", error);
      }
    };
    fetchData();
  }, []);

  function isNumeric(value) {
    return /^-?\d+$/.test(value);
  }


  const requiredFields = (fields, values) => {
    for (const field of fields) {
      if (!values[field]) {
        return true;
      }
    }
    return false;
  };

  const messageOTP = t("Please Enter the OTPs Generated From Fatoora Portal")
  const messageAll = t("Please Complete the Form with Valid Details.")

  const handleSave = (values) => {
    setIsSending(true)

    if (requiredFields(['otp', 'otp2'], values)) {
      NotificationManager.error(messageOTP);
      setIsSending(false);
      return;
    }

    if (requiredFields(['type_of_business_title', 'vat', 'id_type', 'id_number', 'common_name', 'provider_name', 'version', 'eg_serial_number', 'organization', 'organization_unit', 'street', 'business_category', 'city', 'city_subdiv', 'plot', 'postal', 'building'], values)) {
      NotificationManager.error(messageAll);
      setIsSending(false);
      return;
    }

    if (values.vat[10] === "1" && !(isNumeric(values.organization_unit) && !values.organisation_unit.length === 10)) {
      NotificationManager.error(t('The organisation unit name must be 10 digits because your VAT has "1" as a 11th digit'));
      setIsSending(false);
      return;
    }

    values = { ...values, serial_number: `1-${values.provider_name}|2-${values.version}|3-${values.eg_serial_number}` }

    zatca.post("sdk/zatca_connect", values)
      .then(response => {

        if (response.data.basic_csid) {
          NotificationManager.success("CCSID Issued Successfully");
        }

        if (response.data.production_csid) {
          NotificationManager.success("PCSID Issued Successfully");
        }
        localStorage.removeItem('zatca_form')
        setIsSending(false)
        setTimeout(() => {
          history.push("/zatca/details/" + response.data.id);
        }, 800);
      })
      .catch((error) => {
        setIsSending(false)
        NotificationManager.error(error.response.data.errors);
      });
  };

  const businessOptions = [
    { value: '0100', label: t('B2B') },
    { value: '1000', label: t('B2C') },
    { value: '1100', label: t('Both') }
  ];

  const csrType = [
    { value: t('Production') },
    { value: t('Sandbox') },
    { value: t('Simulation') }
  ];

  let time_out

  if (!organisation)
    return null

  return (
    <Page title={t("Zatca Onboarding For E-Invoicing")} className="inv-table">
      <GetBack />
      {!data && <LoadingMsg />}
      {data &&
        <LocalForm
          onChange={(form) => {
            clearTimeout(time_out)
            time_out = setTimeout(() => { localStorage.setItem('zatca_form', btoa(JSON.stringify(form))) }, 1000);
            setData(form);
          }}
          initialState={localStorage.getItem('zatca_form') ? JSON.parse(atob(localStorage.getItem('zatca_form'))) : {}}
          onSubmit={values => handleSave(values)}>
          <div className="text-center my-3">
            <span>
              <Trans>Welcome to Zatca Integration of Hesaab, Please Log in to your
                <a href="https://fatoora.zatca.gov.sa/" target="_blank" className="text-success"> Fatoora Portal </a>
                and generate two OTP's to begin.
              </Trans>
            </span>
          </div>
          <div className="mb-4 alert alert-info" role="alert">
            <Row>
              <Col className="col-md-6 py-1">
                <FormGroup label={t("OTP 1*")} model=".otp">
                  <Control.text model=".otp" placeholder="123345" maxLength="6" />
                </FormGroup>
              </Col>
              <Col className="col-md-6 py-1">
                <FormGroup label={t("OTP 2*")} model=".otp2">
                  <Control.text model=".otp2" placeholder="123345" maxLength="6" />
                </FormGroup>
              </Col>
            </Row>
          </div>
          <div className="border p-2 my-2 shadow-sm bg-white rounded">
            <Row>
              {/* ---------------------------------- */}
              <Col className="col-md-4 py-1">
                <FormGroup label={t("Common Name (CN)") + "*"} model=".common_name">
                  <Control.text model=".common_name" placeholder={t("e.g. Device RUH001")}/>
                  <span className="hint small text-muted">*<Trans>Name or Asset Tracking Number for the Solution Unit</Trans></span>
                </FormGroup>
              </Col>
              <Col className="col-md-4 py-1">
                <FormGroup label={t("Organization Name") + "*"} model=".organization">
                  <Control.text model=".organization" placeholder={t("e.g. Hesaab Accounting Company")} defaultValue={organisation?.LegalName}/>
                  <span className="hint small text-muted">*<Trans>Name as per VAT Registration Certificate or Trade Name</Trans></span>
                </FormGroup>
              </Col>
              <Col className="col-md-4 py-1">
                <FormGroup label={t("Organization Unit") + "*"} model=".organization_unit">
                  <Control.text model=".organization_unit" placeholder={t("e.g. Riyadh Branch")} />
                  <span className="hint small text-muted">*<Trans>The branch name for taxpayers and in case of VAT Groups this field should contain the 10-digit</Trans></span>
                </FormGroup>
              </Col>
            </Row>
            <Row>
              {/* ---------------------------------- */}
              <Col className="col-md-4 py-1">
                <FormGroup label={t("Tax Registration No.") + "*"} model=".vat">
                  <Control.text model=".vat" placeholder={t("e.g. 3xxxxxxxxxx0003")} maxLength={15} defaultValue={organisation?.TaxNumber}/>
                </FormGroup>
              </Col>
              <Col className="col-md-4 py-1">
                <FormGroup label={t("Business Category") + "*"} model=".business_category">
                  <Control.text model=".business_category" placeholder={t("e.g. Accounting")} />
                </FormGroup>
              </Col>
              </Row>
            <Row>
              <Col className="col-md-4 py-1">
                <FormGroup label="ID Type" model=".id_type" className="pt-2 pb-2">
                  <Control
                    component={Select}
                    model=".id_type"
                    labelKey="label"
                    valueKey="id"
                    placeholder={t("Select...")}
                    options={idTypes}
                    simpleValue
                    className="mb-2" style={{ backgroundColor: '#F4F7FE' }}
                  />
                </FormGroup>
              </Col>
              {data.id_type === 'OTH' && <Col className="col-md-4 py-1">
                <FormGroup label="ID Name" model=".id_name" className="pt-2 pb-2">
                  <Control.text
                    updateOn="blur"
                    model=".id_name"
                    placeholder={t("ID Name")}
                    className="mb-2 pt-2 pb-2"
                  />
                </FormGroup>
              </Col>}
              <Col className="col-md-4 py-1">
                <FormGroup label="ID Number" model=".id_number" className="pt-2 pb-2">
                  <Control.text
                    updateOn="blur"
                    model=".id_number"
                    placeholder={t("ID Number")}
                    className="form-control"
                  />
                </FormGroup>
              </Col>
            </Row>
            <Row>
              {/* ---------------------------------- */}
              <div className="col-md-12 py-1">
                <div className="row">
                  <Col className="col-md-4 py-1">
                    <FormGroup label={t("EGS Solution Provider Name: ") + "*"} model=".provider_name">
                      <Control.text model=".provider_name" placeholder={t("e.g. XYZ Technologies")} />
                    </FormGroup>
                  </Col>
                  <Col className="col-md-4 py-1">
                    <FormGroup label={t("EGS Model or Version") + "*"} model=".version">
                      <Control.text model=".version" placeholder={t("e.g. Version 2.0")} />
                    </FormGroup>
                  </Col>
                  <Col className="col-md-4 py-1">
                    <FormGroup label={t("EGS Serial Number") + "*"} model=".eg_serial_number">
                      <Control.text model=".eg_serial_number" placeholder={t("e.g. XYZ12345678")} />
                    </FormGroup>
                  </Col>
                </div>
              </div>
            </Row>
          </div>
          <div className="border p-2 my-2 shadow-sm bg-white rounded">
            <Row className="">
              <Col className="col-md-4 py-1">
                <FormGroup label={t("City Address*")} model=".Address">
                  <Control.text model=".city" placeholder={t("City")} className="my-1" defaultValue={City}/>
                  <Control.text model=".city_subdiv" placeholder={t("Sub division")} className="my-1" defaultValue={Region}/>
                </FormGroup>
              </Col>
              <Col className="col-md-4 py-1">
                <FormGroup label={t("Street Address*")} >
                  <Control.text model=".street" placeholder={t("Street")} className="my-1" defaultValue={AddressLine1}/>
                  <Control.text model=".postal" placeholder={t("Postal Code (5 Digits)")} maxLength="5" minLength={5} className="my-1" defaultValue={PostalCode} />
                </FormGroup>
              </Col>
              <Col className="col-md-4 py-1">
                <FormGroup label={t("Area Address*")}>
                  <Control.text model=".plot" placeholder={t("Plot Number (4 Digits)")} maxLength="4" minLength={4} className="my-1" defaultValue={AddressLine3}/>
                  <Control.text model=".building" placeholder={t("Building Number (4 Digits)")} maxLength="4" minLength={4} className="my-1" defaultValue={AddressLine4}/>
                </FormGroup>
              </Col>

            </Row>
          </div>
          <div className="border p-2 my-2 shadow-sm bg-white rounded">
            <Row>
              <Col className="col-md-4 py-1">
                <FormGroup label={t("Type of Business") + "*"} model=".type_of_business_title">
                  <Control.select model=".type_of_business_title" defaultValue={businessOptions?.[2].value} >
                    {businessOptions.map((i) => (
                      <option key={i.value} value={i.value}>
                        {i.label}
                      </option>
                    ))}
                  </Control.select>
                </FormGroup>
              </Col>
              <Col className="col-md-4 py-1">
                <FormGroup label={t("CSR Type*")} model=".csr_type">
                  <Control.select model=".csr_type" defaultValue={csrType?.[0].value} >
                    {csrType.map((i) => (
                      <option key={i.value} value={i.value}>
                        {i.value}
                      </option>
                    ))}
                  </Control.select>
                </FormGroup>
              </Col>
              <Col className="col-md-4 py-1">
                <FormGroup label={t("Country Code") + "*"} model=".country_code" hidden>
                  <Control.text model=".country_code" defaultValue="SA" disabled />
                </FormGroup>
              </Col>
            </Row>
          </div>
          <div className="tw-flex my-4">
            {!isSending ?
              <Button color="primary"> <Trans>Onboard</Trans> </Button> :
              <Button color="primary" disabled><span className="fa fa-spinner fa-spin" /><Trans>Onboarding</Trans></Button>
            }
          </div>
        </LocalForm>
      }
    </Page >
  );
};

export default RegistrationSDK;
